import { countryNameToCode, normalizeCountryCode } from "@/lib/utils/country-code";

type GeolocationResult = {
	country: string | null;
	countryCode: string | null;
};

function sanitizeCountryName(value?: string | null): string | null {
	const country = value?.trim();
	if (!country || country.toLowerCase() === "undefined" || country.toLowerCase() === "null") {
		return null;
	}
	return country;
}

function sanitizeCountryCode(code?: string | null, country?: string | null): string | null {
	return (
		normalizeCountryCode(code) ||
		countryNameToCode(code) ||
		countryNameToCode(country) ||
		null
	);
}

let geolocationPromise: Promise<GeolocationResult> | null = null;
const AUTH_GEOLOCATION_STORAGE_KEY = "techxeed-auth-geolocation";

/**
 * Utility function to detect user's country based on IP geolocation.
 * Calls our first-party API route so browser privacy/CSP settings do not block direct third-party requests.
 */
export async function detectCountryFromGeolocation(): Promise<GeolocationResult> {
	if (geolocationPromise) return geolocationPromise;

	geolocationPromise = (async () => {
		try {
			const response = await fetch("/api/geolocation", { cache: "no-store" });

			if (!response.ok) {
				console.warn("Geolocation API request failed:", response.status);
				return { country: null, countryCode: null };
			}

			const data = await response.json();
			const country = sanitizeCountryName(typeof data.country === "string" ? data.country : null);
			const countryCode = sanitizeCountryCode(
				typeof data.countryCode === "string" ? data.countryCode : null,
				country,
			);
			return { country, countryCode };
		} catch (error) {
			console.warn("Error detecting country:", error);
			return { country: null, countryCode: null };
		}
	})();

	return geolocationPromise;
}

export async function detectCountryFromIP(): Promise<string | null> {
	try {
		const data = await detectCountryFromGeolocation();
		return data.country;
	} catch (error) {
		console.warn("Error detecting country:", error);
		return null;
	}
}

export async function detectCountryCodeFromIP(): Promise<string | null> {
	try {
		const data = await detectCountryFromGeolocation();
		return data.countryCode;
	} catch {
		return null;
	}
}

export async function rememberDetectedCountryForAuth(): Promise<void> {
	if (typeof window === "undefined") return;

	const detected = await detectCountryFromGeolocation();
	if (!detected.country && !detected.countryCode) return;

	sessionStorage.setItem(AUTH_GEOLOCATION_STORAGE_KEY, JSON.stringify(detected));
}

export function getRememberedCountryForAuth(): GeolocationResult {
	if (typeof window === "undefined") return { country: null, countryCode: null };

	try {
		const raw = sessionStorage.getItem(AUTH_GEOLOCATION_STORAGE_KEY);
		if (!raw) return { country: null, countryCode: null };

		const parsed = JSON.parse(raw) as Partial<GeolocationResult>;
		const country = sanitizeCountryName(typeof parsed.country === "string" ? parsed.country : null);
		const countryCode = sanitizeCountryCode(
			typeof parsed.countryCode === "string" ? parsed.countryCode : null,
			country,
		);
		return { country, countryCode };
	} catch {
		return { country: null, countryCode: null };
	}
}

export function getRememberedAuthLocationFields(): { country?: string; countryCode?: string } {
	const detected = getRememberedCountryForAuth();
	return {
		...(detected.country ? { country: detected.country } : {}),
		...(detected.countryCode ? { countryCode: detected.countryCode } : {}),
	};
}

export function clearRememberedCountryForAuth(): void {
	if (typeof window === "undefined") return;
	sessionStorage.removeItem(AUTH_GEOLOCATION_STORAGE_KEY);
}

